if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      connectedCallback() {
        this.addEventListener('change', this.onChange.bind(this));
      }

      onChange(event) {
        const productInfo = this.closest('product-info');
        if (!productInfo || !this.contains(event.target)) return;

        productInfo.handleOptionValueChange({
          target: event.target,
          selectedOptionValues: this.selectedOptionValues,
        });
      }

      get selectedOptionValues() {
        return Array.from(
          this.querySelectorAll('input[type="radio"][data-option-value-id]:checked')
        ).map((input) => input.dataset.optionValueId);
      }
    }
  );
}

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      connectedCallback() {
        this.abortController = null;
        this.initGallery();
        this.initProductForm();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      handleOptionValueChange({ target, selectedOptionValues }) {
        const productUrl = this.dataset.url;
        const sectionId = this.dataset.section;
        const params = [`section_id=${sectionId}`];

        if (selectedOptionValues.length) {
          params.push(`option_values=${selectedOptionValues.join(',')}`);
        }

        this.pendingTargetId = target.id;
        this.fetchSection(`${productUrl}?${params.join('&')}`);
      }

      handleVariantSelectChange(select) {
        const productUrl = this.dataset.url;
        const sectionId = this.dataset.section;
        const variantId = select.value;

        if (!variantId) return;

        this.pendingTargetId = select.id;
        this.fetchSection(`${productUrl}?section_id=${sectionId}&variant=${variantId}`);
      }

      fetchSection(requestUrl) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sectionId = this.dataset.section;
            const source =
              html.querySelector(`product-info[data-section="${sectionId}"]`) ||
              html.querySelector(`#shopify-section-${sectionId} product-info`);

            if (!source) return;

            const openDetails = Array.from(this.querySelectorAll('details[open]')).map(
              (details) => details.dataset.detailsId
            );

            this.innerHTML = source.innerHTML;

            openDetails.forEach((id) => {
              if (!id) return;
              const details = this.querySelector(`details[data-details-id="${id}"]`);
              if (details) details.open = true;
            });

            const variant = this.getSelectedVariant();
            this.updateURL(variant?.id);
            this.initGallery();
            this.initProductForm();

            if (this.pendingTargetId) {
              document.getElementById(this.pendingTargetId)?.focus();
              this.pendingTargetId = null;
            }

            // Generic contract: bubbling DOM event so app extensions can react
            // to variant changes without coupling to a module pub/sub.
            // `variant` is Shopify's variant JSON (price in cents), or null.
            this.dispatchEvent(
              new CustomEvent('variant:change', {
                bubbles: true,
                detail: { variant },
              })
            );
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
            }
          });
      }

      getSelectedVariant() {
        const json = this.querySelector('[data-selected-variant]')?.textContent;
        return json ? JSON.parse(json) : null;
      }

      updateURL(variantId) {
        if (this.dataset.updateUrl === 'false') return;

        const url = new URL(window.location.href);
        if (variantId) {
          url.searchParams.set('variant', variantId);
        } else {
          url.searchParams.delete('variant');
        }
        window.history.replaceState({}, '', url.toString());
      }

      initGallery() {
        this.querySelectorAll('[data-gallery]').forEach((gallery) => {
          const slides = gallery.querySelectorAll('.pdp__slide');
          const thumbs = gallery.querySelectorAll('[data-thumb]');
          const thumbsTrack = gallery.querySelector('[data-thumbs-track]');
          const thumbsPrev = gallery.querySelector('[data-thumbs-prev]');
          const thumbsNext = gallery.querySelector('[data-thumbs-next]');
          const current = gallery.querySelector('[data-gallery-current]');
          const total = slides.length;
          let idx = Number.parseInt(gallery.dataset.featuredSlide, 10);

          if (Number.isNaN(idx)) idx = 0;

          const updateThumbScrollState = () => {
            if (!thumbsTrack) return;
            const canPrev = thumbsTrack.scrollLeft > 1;
            const canNext =
              thumbsTrack.scrollLeft + thumbsTrack.clientWidth < thumbsTrack.scrollWidth - 1;
            if (thumbsPrev) thumbsPrev.hidden = !canPrev;
            if (thumbsNext) thumbsNext.hidden = !canNext;
          };

          const scrollActiveThumbIntoView = (index) => {
            if (!thumbsTrack) return;
            const thumb = thumbs[index];
            if (!thumb) return;
            const target = thumb.offsetLeft - (thumbsTrack.clientWidth - thumb.clientWidth) / 2;
            thumbsTrack.scrollTo({ left: target, behavior: 'smooth' });
          };

          const scrollThumbs = (direction) => {
            if (!thumbsTrack) return;
            const amount = thumbsTrack.clientWidth * 0.8;
            thumbsTrack.scrollBy({
              left: direction === 'prev' ? -amount : amount,
              behavior: 'smooth',
            });
          };

          const setActive = (i) => {
            idx = (i + total) % total;
            slides.forEach((slide, j) => {
              slide.classList.toggle('is-active', j === idx);
            });
            thumbs.forEach((thumb, j) => {
              thumb.classList.toggle('is-active', j === idx);
            });
            if (current) current.textContent = String(idx + 1);
            scrollActiveThumbIntoView(idx);
            updateThumbScrollState();
          };

          setActive(idx);

          thumbs.forEach((thumb) => {
            thumb.addEventListener('click', () => {
              const i = Number.parseInt(thumb.getAttribute('data-thumb'), 10);
              setActive(i);
            });
          });

          if (thumbsTrack) {
            thumbsTrack.addEventListener('scroll', updateThumbScrollState, { passive: true });
            if (typeof ResizeObserver !== 'undefined') {
              const observer = new ResizeObserver(updateThumbScrollState);
              observer.observe(thumbsTrack);
            }
            updateThumbScrollState();
          }

          if (thumbsPrev) {
            thumbsPrev.addEventListener('click', () => scrollThumbs('prev'));
          }
          if (thumbsNext) {
            thumbsNext.addEventListener('click', () => scrollThumbs('next'));
          }
        });

        const variantSelect = this.querySelector('[data-variant-select]');
        if (variantSelect && variantSelect.dataset.bound !== 'true') {
          variantSelect.dataset.bound = 'true';
          variantSelect.addEventListener('change', () => {
            this.handleVariantSelectChange(variantSelect);
          });
        }
      }

      initProductForm() {
        const form = this.querySelector('[data-product-form]');
        if (!form || form.dataset.cartBound === 'true') return;

        form.dataset.cartBound = 'true';
        form.addEventListener('submit', this.onProductFormSubmit.bind(this));
      }

      onProductFormSubmit(event) {
        event.preventDefault();

        const form = event.currentTarget;
        const submitButton = form.querySelector('[type="submit"]');
        const root = window.Shopify?.routes?.root || '/';
        const addUrl = `${root.endsWith('/') ? root : `${root}/`}cart/add.js`;
        const cartUrl = `${root.endsWith('/') ? root : `${root}/`}cart.js`;

        this.setSubmitLoading(submitButton, true);

        fetch(addUrl, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((data) => Promise.reject(data));
            }
            return response.json();
          })
          .then(() => fetch(cartUrl, { headers: { Accept: 'application/json' } }))
          .then((response) => response.json())
          .then((cart) => {
            document.querySelectorAll('[data-cart-count]').forEach((badge) => {
              if (cart.item_count > 0) {
                badge.textContent = String(cart.item_count);
                badge.hidden = false;
              } else {
                badge.hidden = true;
              }
            });
            document.dispatchEvent(
              new CustomEvent('cart:updated', { bubbles: true, detail: { cart } })
            );
            document.dispatchEvent(new CustomEvent('cart:open'));
          })
          .catch((error) => {
            const message =
              error?.description ||
              error?.message ||
              this.dataset.addErrorLabel ||
              'Could not add this product to your cart';
            window.alert(message);
          })
          .finally(() => {
            this.setSubmitLoading(submitButton, false);
          });
      }

      setSubmitLoading(button, loading) {
        if (!button) return;

        if (loading) {
          if (!button.dataset.defaultLabel) {
            button.dataset.defaultLabel = button.textContent.trim();
          }
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          const label = this.dataset.addingLabel || 'Adding…';
          button.innerHTML =
            '<span class="pdp__cta-spinner" aria-hidden="true"></span><span>' + label + '</span>';
          return;
        }

        button.setAttribute('aria-busy', 'false');
        if (button.dataset.defaultLabel) {
          button.textContent = button.dataset.defaultLabel;
        }

        const variantSelect = this.querySelector('[data-variant-select]');
        const selectedOption = variantSelect?.selectedOptions?.[0];
        button.disabled = Boolean(selectedOption?.disabled);
      }
    }
  );
}
